import { useApolloClient } from "@apollo/client/react";
import { Session } from "@ory/client-fetch";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getToken, storeToken } from "./authToken";
import createOryClient from "./createOryClient";
import { LogoutDocument } from "./Logout.generated";
import useOrySession from "./useOrySession";
import useRegistrationStatus, {
  RegistrationStatus,
} from "./useRegistrationStatus";

type Options = {
  // Kratos public API origin; Ory auth is disabled without one
  oryOrigin: string | undefined;
  // where Kratos sends the browser after an Ory logout
  logoutReturnTo: string;
  // reports unexpected errors (e.g. to Sentry) without failing auth
  onError: (error: unknown) => void;
  // app-specific cleanup for a Malan logout, run right after the token clears
  onMalanLogout?: (tokenError?: boolean) => void;
};

export type AuthClient = {
  token: string;
  setToken: (token: string) => void;
  orySession?: Session;
  loadingOrySession: boolean;
  isLoggedIn: boolean;
  // first-sign-in SSO registration; render the app only once it's "done"
  registrationStatus: RegistrationStatus;
  logout: (tokenError?: boolean) => Promise<void>;
};

// Malan token and Ory session state, with the logout sequence for both. Uses
// the Apollo client from context.
export default function useAuthClient({
  oryOrigin,
  logoutReturnTo,
  onError,
  onMalanLogout,
}: Options): AuthClient {
  const client = useApolloClient();
  const oryClient = useMemo(() => createOryClient(oryOrigin), [oryOrigin]);
  const [token, setTokenState] = useState<string>(() => getToken());

  // Callers pass inline callbacks; read the latest through refs so they
  // never change the identity of logout.
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;
  const onMalanLogoutRef = useRef(onMalanLogout);
  onMalanLogoutRef.current = onMalanLogout;
  const reportError = useCallback(
    (error: unknown) => onErrorRef.current(error),
    []
  );

  const { orySession, loadingOrySession, oryLogout } = useOrySession(
    oryClient,
    logoutReturnTo,
    reportError
  );

  const registrationStatus = useRegistrationStatus(
    orySession,
    !!token,
    reportError
  );

  // save token in the correct order. it must save to sessionStorage
  // first, before updating React state and triggering changes.
  const setToken = useCallback((str: string) => {
    storeToken(str);
    setTokenState(str);
  }, []);

  const logout = useCallback(
    async (tokenError?: boolean): Promise<void> => {
      if (token) {
        // malan logout
        setToken("");
        onMalanLogoutRef.current?.(tokenError);
        await client.clearStore();
        try {
          await client.mutate({
            mutation: LogoutDocument,
            variables: { input: {} },
          });
        } catch (e) {
          // report but do not fail
          reportError(e);
        }
      } else {
        // ory logout (redirects to the Ory logout URL)
        await client.clearStore();
        oryLogout();
      }
    },
    [client, token, setToken, oryLogout, reportError]
  );

  // SC-9885: handle expired sessions in apps
  useEffect(() => {
    if (!orySession?.expires_at) return;

    const expirationTime = new Date(orySession.expires_at);
    const now = new Date();

    if (expirationTime <= now) {
      void logout();
      return;
    }

    const timeUntilExpiration = expirationTime.getTime() - now.getTime();
    const timeoutId = setTimeout(() => {
      void logout();
    }, timeUntilExpiration);

    return () => clearTimeout(timeoutId);
  }, [orySession?.expires_at, logout]);

  return useMemo(
    (): AuthClient => ({
      token,
      setToken,
      orySession,
      loadingOrySession,
      isLoggedIn:
        !!token ||
        !!(orySession?.expires_at && orySession.expires_at > new Date()),
      registrationStatus,
      logout,
    }),
    [token, setToken, orySession, loadingOrySession, registrationStatus, logout]
  );
}
